'use client';

import { ChangeEventHandler, useCallback, useState } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';

export interface SliderProps {
  css?: Styles;
  name?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  marks: number[];
  onChange?: (value: number) => void;
  getLabel?: (value: number) => string;
}

export const Slider = ({
  css: cssProps,
  name,
  min = 0,
  max = 100,
  step = 1,
  value = 0,
  marks,
  onChange = () => {},
  getLabel = String,
}: SliderProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => onChange(Number(e.currentTarget.value)),
    [onChange],
  );

  const onPointerDown = () => {
    setIsDragging(true);

    const onPointerUp = () => {
      setIsDragging(false);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };

    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
  };

  const ratio = normalize(min, max, value);
  const percent = toPercent(ratio);

  return (
    <div className={css(outerContainerStyle, cssProps)}>
      <div className={innerContainerStyle}>
        <div className={barStyle}>
          <div className={barFillStyle} style={{ width: percent }} />
          <div className={marksContainerStyle}>
            {marks.map((mark) => (
              <Mark key={mark} percent={toPercent(normalize(min, max, mark))} on={mark <= value} />
            ))}
          </div>
        </div>
        <div className={thumbStyle} style={{ left: `calc(${handleSize / 2}px + (100% - ${handleSize}px) * ${ratio})` }}>
          {isDragging && <div className={tooltipStyle}>{getLabel(value)}</div>}
        </div>
        <input
          type='range'
          name={name}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onInputChange}
          onPointerDown={onPointerDown}
          tabIndex={-1}
          className={inputStyle}
        />
      </div>
    </div>
  );
};

const barHeight = 4;
const markSize = 10;
const handleSize = 14;
const tooltipHeight = 18;
const tooltipGap = 4;

const outerContainerStyle = css.raw({
  pos: 'relative',
  width: '100%',
  h: handleSize,
});

const innerContainerStyle = css({
  pos: 'relative',
  w: '100%',
  h: handleSize,
});

const barStyle = css({
  pos: 'absolute',
  top: (handleSize - barHeight) / 2,
  left: handleSize / 2,
  right: handleSize / 2,
  h: barHeight,
  touchAction: 'none',
  bg: 'background.default.level-2',
  rounded: 999,
});

const barFillStyle = css({
  pos: 'absolute',
  left: 0,
  height: '100%',
  bg: 'inverse.white',
  rounded: 999,
});

const marksContainerStyle = css({
  pos: 'absolute',
  top: (barHeight - markSize) / 2,
  left: 0,
  w: '100%',
  h: markSize,
});

const thumbStyle = css({
  pos: 'absolute',
  top: 0,
  w: handleSize,
  h: handleSize,
  boxSizing: 'border-box',
  rounded: 999,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: 'inverse.white',
  bg: 'background.default.level-0',
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
  zIndex: 1,
});

const tooltipStyle = css({
  pos: 'absolute',
  left: '50%',
  bottom: `calc(100% + ${tooltipGap}px)`,
  w: 35,
  h: tooltipHeight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'inverse.white',
  color: 'inverse.black',
  textStyle: 'Text/XS/Monospace/Regular',
  rounded: 4,
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
});

const inputStyle = css({
  pos: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  w: '100%',
  h: handleSize,
  appearance: 'none',
  bg: 'transparent',
  outline: 'none',
  cursor: 'pointer',
  zIndex: 2,
  _focus: { outline: 'none' },
  '&::-webkit-slider-thumb': {
    w: handleSize,
    h: handleSize,
    appearance: 'none',
    cursor: 'grab',
    // Invisible hit target; visual thumb is rendered separately
    bg: 'transparent',
    border: 'none',
    rounded: 999,
  },
  '&::-webkit-slider-runnable-track': {
    w: '100%',
    h: '100%',
    bg: 'transparent',
  },
  '&::-moz-range-thumb': {
    w: handleSize,
    h: handleSize,
    cursor: 'grab',
    bg: 'transparent',
    border: 'none',
    rounded: 999,
  },
  '&::-moz-range-track': {
    bg: 'transparent',
  },
});

interface MarkProps {
  percent: string;
  on: boolean;
}

const Mark = ({ percent, on }: MarkProps) => {
  return <div className={cx(markStyle, on ? markOnStyle : markOffStyle)} style={{ left: percent }} />;
};

const markStyle = css({
  position: 'absolute',
  top: 0,
  w: markSize,
  h: markSize,
  rounded: 999,
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
  boxSizing: 'border-box',
});

const markOnStyle = css({
  bg: 'inverse.white',
  borderWidth: 0,
  borderStyle: 'solid',
  borderColor: 'transparent',
});

const markOffStyle = css({
  bg: 'background.default.level-3',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'border.default.level-1',
});

function toPercent(ratio: number): string {
  return `${ratio * 100}%`;
}

function normalize(start: number, end: number, value: number): number {
  if (end === start) return 0;
  return (value - start) / (end - start);
}
